import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { kUserFilesFoldersApiBasePath } from '../constants'
import { FolderResponse, FolderResponseSchema } from '../schemas'

/**
 * Restore a soft-deleted folder and all items deleted with it.
 *
 * Only directly-deleted folders can be restored (not cascade-deleted ones).
 * The parent folder must not be deleted.
 *
 * @param folderId - The folder ID to restore (UUID)
 * @returns FolderResponse containing the restored folder
 * @throws Error when user is not authenticated
 * @throws ApiError when the restore-folder request fails
 */
export async function restoreFolder(folderId: string): Promise<FolderResponse> {
  const sentryTransactionId = nanoid()

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesFoldersApiBasePath}/${encodeURIComponent(folderId)}/restore`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentryTransactionId,
      },
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)

      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, url),
            folderId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })

      throw new ApiError(response.status, detail || `Failed to restore folder: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Restore folder response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            folderId,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid restore folder response JSON')
    }

    const parsed = FolderResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Restore folder response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            folderId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid restore folder response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { folderId },
        })
      })
    }
    throw error
  }
}
